import {
  acceptsDraw,
  type ArenaAiSkill,
  type DuelLevel,
  CARD_CHOICE_SECONDS,
  CARD_REVEAL_SECONDS,
  applyBattleResult,
  cellAt,
  applyMove,
  autoResolveBattle,
  boardAiClock,
  boardAiOptions,
  chooseCardSupport,
  cardBattleMainScores,
  eligibleSupport,
  initializeCards,
  chooseMusterResponse,
  timeoutMove,
  legalMoves,
  resolveCardBattle,
  supportCardName,
  supportFitDescription,
  supportRole,
  replenishCards,
  resolveRollBattle,
  duelLevelForSkill,
  createGame,
  destinationsFor,
  duelLevelSkill,
  parseBoard,
  pieceById,
  spellMoves,
  replayHistory,
  type BattleResult,
  type CardChoice,
  type CardRole,
  type CardScore,
  type BattleStart,
  type BoardDef,
  type EndReason,
  type FactionId,
  type FighterId,
  type FighterInput,
  type GameContext,
  type GameDefinition,
  type GameState,
  type HistoryEntry,
  type Move,
  type PieceId,
  type Roster,
  type ServerMessage,
  type SpellId,
  type SupportCardId,
  type TickBundle,
  ALL_SPELLS,
  SPELL_COSTS,
  canAfford,
  knowsSpell,
  maxSpellPoints,
} from '@archon3d/core';
import type { ConnectionState, RoomEvent } from '../net/roomClient';
import { TickBuffer } from '../net/tickBuffer';
import { CancelledThink, localBrain, type Brain, type BrainResult } from './brain';
import { createLockstepLink, type LockstepLink } from './lockstepLink';
import type { SeatDevice } from './controls';
import { Countdown, type ClockView } from './countdown';
import { msAfterStep, storyEnd, tellCardClash, type CardStory } from './cardStory';

/**
 * A lone person's seconds to choose a Card support against the AI: with no hand to hide from a
 * person across the table, the clock only rushes a newcomer (D-41, THE_DECISION_TABLE #4). At one
 * table with two people, and online, it stays `CARD_CHOICE_SECONDS`.
 */
export const CARD_CHOICE_SECONDS_ALONE = 45;

/**
 * How long the AI takes over its Card support when a person is in the clash: a moment, as a
 * player would, never at once (D-41). Its card goes down face down when the time is up.
 */
export const AI_CARD_CHOICE_MS = { min: 1100, spread: 900 } as const;
import { boardSideName, sideName } from './sideName';

/** Who plays a faction at this client: a person here, the AI, or a person at another client. */
export type Seat = 'human' | 'ai' | 'remote';

/** The room server as the session sees it; `RoomClient` provides it, tests fake it. */
export interface RoomLink {
  /** Seat (faction index) of the local player. */
  readonly seat: number;
  sendMove(move: Move): void;
  sendCardChoice?(cardId: SupportCardId | null, role?: CardRole): void;
  sendCardContinue?(): void;
  sendInput(tick: number, input: FighterInput): void;
  sendForfeit(): void;
  /** Takes the local seat back from the AI that stood in for this person. */
  reclaim?(): void;
  /** Offers the other sides a draw, on the local person's turn (D-46). */
  sendDrawOffer?(): void;
  /** Answers the draw offered to the local person's side. */
  sendDrawAnswer?(accept: boolean): void;
  /** Server messages in arrival order, plus the connection's own state changes. */
  subscribe(listener: (event: RoomEvent) => void): () => void;
}

/** An online game as the server started it. */
export interface OnlineOptions {
  link: RoomLink;
  /** Player names by seat, empty for AI seats. */
  names: string[];
  /** What happened before this client (re)joined. */
  history?: HistoryEntry[];
  /** A duel already running when this client rejoined, with its ticks so far. */
  battle?: BattleStart & { ticks: TickBundle[] };
  /** Seats the AI stands in for when this client (re)joined; this client's own among them. */
  standIns?: number[];
  /** Seats whose people had dropped when this client (re)joined, and the seconds each had left. */
  paused?: { seat: number; secondsLeft: number }[];
}

/**
 * A seat whose person dropped: the game waits `secondsLeft` from `at` (`performance.now`) for
 * them, then the AI stands in.
 */
export interface DroppedSeat {
  seat: number;
  name: string;
  secondsLeft: number;
  at: number;
}

/** How the online game is doing, for the banner over the table. */
export interface OnlineStatus {
  connection: ConnectionState;
  /** This client has no seat: it watches (from the start, or after losing its seat). */
  spectator: boolean;
  /** The chess clock of the seat to move, as the server last told it; never counted here. */
  clock: { seat: number; name: string; secondsLeft: number; mine: boolean } | null;
  /** Every seat whose person dropped and is being waited for, in the order they dropped. */
  paused: DroppedSeat[];
  /**
   * When this client's own connection dropped (`performance.now`), until the server's catch-up
   * after the resume has arrived: its pane holds the screen until the seat's fate is known.
   */
  lostAt: number | null;
  /** The AI plays this client's seat until the person says they are ready to take it back. */
  standIn: boolean;
  /** Every seat the AI stands in for, this client's own included. */
  standIns: number[];
  /** The room ended for a reason other than the rules. */
  ended: EndReason | null;
  /**
   * This screen's copy of the game no longer follows the server's: a move it was told of does not
   * apply. A reload takes the seat up again from the server (client-session:quality#5).
   */
  desynced: boolean;
  /** The last thing the server refused, or why the connection is gone. */
  notice: string | null;
}

/** Why a spell button is not green: what stands between this piece and casting it. */
export type SpellBlock = 'unknown' | 'poor' | 'circle' | 'held' | 'no-target';

/** One spell as the tablet shows it: what it costs, whether it can be cast, and what stops it. */
export interface SpellButton {
  spell: SpellId;
  cost: number;
  known: boolean;
  affordable: boolean;
  castable: boolean;
  blocked: SpellBlock | null;
}

export interface SessionOptions {
  definition?: GameDefinition;
  board: BoardDef;
  roster: Roster;
  seats: Record<FactionId, Seat>;
  seed: number;
  /** How many of the board's factions play (the last ones are dropped); all of them by default. */
  players?: number;
  /** The factions that play, by id, when any side may sit out; overrides `players`. */
  playing?: FactionId[];
  /** Resume from a state instead of a fresh game. */
  initialState?: GameState;
  /** Fight captures that involve a person in the arena instead of rolling for them. */
  arenaBattles?: boolean;
  /**
   * How captures are settled: `arena` (a person fights in first person, the AI's own fights are
   * rolled), `watch` (every fight plays in the arena with the AI on both sides) or `roll`.
   * Overrides `arenaBattles`.
   */
  fights?: 'arena' | 'card' | 'roll' | 'watch';
  humanFriendlyAi?: boolean;
  engagement?: 'immediate' | 'muster';
  /** Pause before an AI move, ms. */
  aiDelay?: number;
  /** Plies the AI looks ahead (1 quick … 4 deep) for seats without their own setting. */
  aiDepth?: number;
  /** The AI's strength per faction: search depth and arena skill. */
  ai?: Record<FactionId, { depth: number; skill: ArenaAiSkill }>;
  /** People's names per faction, for the sign. */
  names?: Record<FactionId, string>;
  /**
   * Each person's controller per faction when several people at this device have one each: the
   * board pad follows the seat on turn, and in the arena every person fights their own side.
   * Absent when one person plays or the people share the device.
   */
  devices?: Record<FactionId, SeatDevice>;
  /** Where AI moves are computed; defaults to the calling thread. */
  brain?: Brain;
  /** How long an auto-resolved battle (AI against AI, or no arena) is shown, ms. */
  battleDelay?: number;
  /** Timer injection for tests. */
  schedule?: (fn: () => void, ms: number) => () => void;
  /**
   * Play through the room server: local moves are sent and applied when echoed, other seats'
   * moves and every battle come from the server. Local play leaves this unset.
   */
  online?: OnlineOptions;
}

export interface CellTarget {
  x: number;
  y: number;
  kind: 'move' | 'attack' | 'spell';
}

/**
 * Multi-step spell targeting: which spell, who is casting it, and what has been picked so far.
 *
 * The caster is fixed when the spell begins — it is the piece that was selected — because picking
 * targets clears the selection, and the move has to say who paid for it.
 */
export interface Targeting {
  spell: SpellId;
  casterId: PieceId;
  pieceId?: number;
  character?: string;
}

/** A capture waiting for the arena; `resolveBattle` continues the game. */
export interface ArenaBattle {
  humanFriendlyAi?: boolean;
  attackerId: PieceId;
  defenderId: PieceId;
  /** The side the local player fights as; `null` when only watching an online duel. */
  humanSide: FighterId | null;
  /**
   * Every side a person at this device fights: both, when two people with a controller each
   * meet; `humanSide` alone otherwise; none when only watching.
   */
  humans: FighterId[];
  /** Which controller drives each of those sides, when the people have one each. */
  devices?: Partial<Record<FighterId, SeatDevice>>;
  seed: number;
  /** Set for an online duel: the arena steps through the server's bundles. */
  lockstep?: LockstepLink;
  /** The arena AI's skill per side it drives, from the seats' settings. */
  skills?: Partial<Record<FighterId, ArenaAiSkill>>;
  /** Online: the level the server scaled each of its AI sides to, for the arena to match. */
  levels?: Partial<Record<FighterId, DuelLevel>>;
}

export interface SessionEvent {
  id: number;
  type: 'move' | 'spell' | 'battle' | 'battle-resolved' | 'ended' | 'new-game' | 'draw';
  text: string;
}

/** The AI takes a moment over a draw it is offered, as a person would (D-41). */
export const AI_DRAW_ANSWER_MS = 1200;

/** A draw offered and not yet answered (D-46). */
export interface DrawView {
  /** The side that offered it. */
  from: FactionId;
  /** The sides yet to answer it, people and the AI alike. */
  waiting: FactionId[];
  /** The side a person at this device is asked to answer for now; null while others answer. */
  asking: FactionId | null;
}

export interface SessionSnapshot {
  ctx: GameContext;
  state: GameState;
  seats: Record<FactionId, Seat>;
  selectedPieceId: number | null;
  targeting: Targeting | null;
  /** Cells the player may click right now. */
  cellTargets: CellTarget[];
  /** Pieces the player may click right now (spell targets). */
  pieceTargets: number[];
  /** True while an AI decides or a battle plays out – input is ignored. */
  busy: boolean;
  /** Set while the arena fights the pending battle. */
  arena: ArenaBattle | null;
  /** Card mode's private selection board or its simultaneous reveal. */
  cardClash?: CardClashView | null;
  /**
   * The Card clash's clock: the seconds left to choose (online, the server's, which also run while
   * the others choose), and those a reveal waits for its people before it goes on by itself.
   */
  cardClock?: ClockView | null;
  muster?: MusterView | null;
  /** A draw offered and not yet answered. */
  draw?: DrawView | null;
  /** The move that produced this state, so the view can animate it; `null` after battles. */
  lastMove: Move | null;
  lastEvent: SessionEvent | null;
  /** Set for an online game. */
  online: OnlineStatus | null;
  /** People's names per faction, where given. */
  names: Record<FactionId, string>;
  /** Each person's controller per faction, where the people at this device have one each. */
  devices?: Record<FactionId, SeatDevice>;
}

export interface MusterView {
  faction: FactionId;
  pieces: { id: PieceId; character: string; name: string }[];
}

export interface CardClashView {
  phase: 'choose' | 'reveal';
  participants: {
    pieceId: PieceId;
    faction: FactionId;
    character: string;
    name: string;
    main: number;
    level?: DuelLevel;
  }[];
  /** Only the current local chooser's legal private hand is exposed. */
  chooser: FactionId | null;
  /** The sides still choosing, people and the AI alike; the others' cards are down, face down. */
  choosing?: FactionId[];
  hand: { id: SupportCardId; name: string; role: CardRole; water: boolean; fit: string }[];
  scores?: CardScore[];
  /**
   * A reveal a person here took part in waits for them: `continue` until they say so, `others`
   * once they have and the room waits for the rest. Absent, the outcome follows in a beat.
   */
  hold?: 'continue' | 'others';
  /**
   * The reveal as it is told: the supports laid down, the chances rolled, the result named. A clash
   * nobody here fought in is told at once, at its end.
   */
  story?: CardStory;
}

/**
 * Drives one game on the client: turns, human input (selection, targeting), AI turns and battles
 * (handed to the arena when a person is involved, rolled otherwise). Pure TypeScript so it is
 * unit-testable; the Babylon view and the React overlay both subscribe to snapshots.
 */
export class GameSession {
  private readonly listeners = new Set<() => void>();
  private snapshot: SessionSnapshot;
  private cancelTimer: (() => void) | null = null;
  private eventId = 0;
  private readonly schedule: (fn: () => void, ms: number) => () => void;
  private readonly aiDelay: number;
  private aiDepth: number;
  private readonly brain: Brain;
  /** Bumped whenever the state changes under a thinking AI, so a stale answer is dropped. */
  private thinkToken = 0;
  /** A think is in flight in the brain (a worker's): cancelled if the game moves on without it. */
  private thinking = false;
  private readonly battleDelay: number;
  private readonly fights: 'arena' | 'card' | 'roll' | 'watch';
  private readonly humanFriendlyAi: boolean;
  private readonly ai: Record<FactionId, { depth: number; skill: ArenaAiSkill }>;
  private readonly playing: FactionId[] | undefined;
  /** Views hold the game while a piece is still travelling; nothing proceeds until released. */
  private holds = 0;
  private readonly online: OnlineOptions | null;
  private unsubscribeRoom: (() => void) | null = null;
  /** Server events not yet applied: they wait for the view like AI moves do. */
  private inbox: ServerMessage[] = [];
  /** When a card prompt waiting in the inbox came (`performance.now`): its clock ran meanwhile. */
  private readonly promptedAt = new WeakMap<ServerMessage, number>();
  /** History entries (moves and battle results) received so far, so a sync adds only the rest. */
  private received = 0;
  /** The duel the server runs, from its announcement until its result is applied. */
  private duel: { start: BattleStart; buffer: TickBuffer } | null = null;
  /** The arena has shown the duel's outcome; the server's result may be applied now. */
  private arenaDone = false;
  private cardChoices: Partial<Record<FactionId, CardChoice>> = {};
  private cardHumans: FactionId[] = [];
  /** The AI sides still taking their moment over a card, and the timers that end it. */
  private aiChoosing = new Set<FactionId>();
  private aiChoiceTimers: (() => void)[] = [];
  private readonly cardClock: Countdown;
  /** A revealed clash's outcome while its cards are up (local play; online the server has it). */
  private revealed: { result: BattleResult; rng: number } | null = null;
  /** The next step of a told clash, due on the session's clock; and whether a page holds it. */
  private storyTimer: (() => void) | null = null;
  private storyHeld = false;
  /** A draw offered at this table and not yet answered (local play; online the server has it). */
  private drawPending: { from: FactionId; waiting: Set<FactionId> } | null = null;
  /** The AI's answer to it while the AI takes its moment. */
  private drawTimer: (() => void) | null = null;
  /** The turn a draw was last offered on from this device: one offer a turn (D-46). */
  private drawOfferedOn = -1;

  constructor(options: SessionOptions) {
    this.schedule =
      options.schedule ??
      ((fn, ms) => {
        const handle = setTimeout(fn, ms);
        return () => clearTimeout(handle);
      });
    this.cardClock = new Countdown(this.schedule, (clock) => this.update({ cardClock: clock }));
    this.aiDelay = options.aiDelay ?? 700;
    this.aiDepth = options.aiDepth ?? 1;
    this.brain = options.brain ?? localBrain;
    this.battleDelay = options.battleDelay ?? 1400;
    this.fights = options.fights ?? (options.arenaBattles ? 'arena' : 'roll');
    this.humanFriendlyAi = options.humanFriendlyAi ?? true;
    this.ai = options.ai ?? {};
    this.playing = options.playing;
    const ctx = {
      combatMode: this.fights,
      cardLevels: Object.fromEntries(
        Object.entries(options.seats)
          .filter(([, seat]) => seat === 'ai')
          .map(([id]) => [id, duelLevelForSkill(this.ai[id]?.skill ?? duelLevelSkill('fair'))]),
      ),
      board: parseBoard(options.board),
      roster: options.roster,
      definition: options.definition,
    };
    this.online = options.online ?? null;
    const state =
      options.initialState ??
      (this.online
        ? replayHistory(
            ctx,
            {
              seed: options.seed,
              players: options.players ?? ctx.board.def.factions.length,
              playing: options.playing,
              engagement: options.engagement,
              // The hands too, as the server holds them (core-net-balance:quality#5).
              cards: this.fights === 'card',
            },
            this.online.history ?? [],
          )
        : createGame(ctx, {
            seed: options.seed,
            players: options.players,
            playing: options.playing,
            engagement: options.engagement,
          }));
    this.snapshot = {
      ctx,
      state,
      seats: options.seats,
      selectedPieceId: null,
      targeting: null,
      cellTargets: [],
      pieceTargets: [],
      busy: false,
      arena: null,
      cardClash: null,
      cardClock: null,
      muster: null,
      draw: null,
      lastMove: null,
      lastEvent: null,
      online: this.online
        ? {
            connection: 'connected',
            spectator: this.online.link.seat < 0,
            clock: null,
            paused: [],
            lostAt: null,
            standIn: false,
            standIns: [],
            ended: null,
            desynced: false,
            notice: null,
          }
        : null,
      names: options.names ?? {},
      devices: options.devices ?? {},
    };
    if (this.online) {
      this.received = this.online.history?.length ?? 0;
      // Seats the AI stood in for when this client joined, its own too after a reload, and the
      // countdowns still running for anyone else.
      this.standIns(this.online.standIns ?? []);
      this.waitingFor(this.online.paused ?? []);
      if (this.online.battle) this.announceDuel(this.online.battle, this.online.battle.ticks);
      this.unsubscribeRoom = this.online.link.subscribe((event) => this.onRoomEvent(event));
    }
    this.continueGame();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): SessionSnapshot {
    return this.snapshot;
  }

  dispose(): void {
    this.cancelTimer?.();
    this.cancelTimer = null;
    this.drawTimer?.();
    this.drawTimer = null;
    this.stopStory();
    this.stopAiChoices();
    this.cardClock.stop();
    this.thinkToken += 1;
    this.stopThinking();
    this.unsubscribeRoom?.();
    this.unsubscribeRoom = null;
    this.listeners.clear();
  }

  /** Whether this game is played through the room server. */
  get isOnline(): boolean {
    return this.online !== null;
  }

  /** The factions that play this game, in board order. */
  get playingFactions(): FactionId[] {
    return (
      this.playing ?? this.snapshot.state.factions.filter((f) => !f.eliminated).map((f) => f.id)
    );
  }

  /** Whether the faction to move is controlled by a person at this client. */
  isHumanTurn(): boolean {
    const { state, seats } = this.snapshot;
    return state.phase === 'play' && seats[state.activeFaction] === 'human';
  }

  /** Input is ignored while an AI thinks, a battle plays out or a view holds the game. */
  private get blocked(): boolean {
    return this.snapshot.busy || this.holds > 0 || !this.isHumanTurn() || this.offline;
  }

  /**
   * Online, with the line down or this copy out of step: whatever is sent now would be lost, and
   * the board locked waiting for its echo. The board, the prompts and the offers wait for the line
   * instead (client-session:quality#4).
   */
  private get offline(): boolean {
    const online = this.snapshot.online;
    return (
      online !== null &&
      (online.connection !== 'connected' || online.lostAt !== null || online.desynced)
    );
  }

  /** A click/tap/ray hit on a piece. */
  pickPiece(pieceId: number): void {
    const s = this.snapshot;
    if (this.blocked) return;
    const piece = pieceById(s.state, pieceId);
    if (!piece) return;

    if (s.targeting) {
      if (s.pieceTargets.includes(pieceId)) this.advanceTargeting({ pieceId });
      return;
    }
    // Clicking an enemy that is a legal attack target moves there.
    const attack = s.cellTargets.find(
      (t) => t.x === piece.x && t.y === piece.y && t.kind === 'attack',
    );
    if (attack && s.selectedPieceId !== null) {
      this.play({ type: 'move', pieceId: s.selectedPieceId, x: piece.x, y: piece.y });
      return;
    }
    if (piece.faction !== s.state.activeFaction) return;
    this.select(s.selectedPieceId === pieceId ? null : pieceId);
  }

  /** A click/tap/ray hit on a board cell. */
  pickCell(x: number, y: number): void {
    const s = this.snapshot;
    if (this.blocked) return;
    const occupant = s.state.pieces.find((p) => p.x === x && p.y === y);
    if (s.targeting) {
      if (occupant && s.pieceTargets.includes(occupant.id))
        this.advanceTargeting({ pieceId: occupant.id });
      else if (s.cellTargets.some((t) => t.x === x && t.y === y)) this.advanceTargeting({ x, y });
      return;
    }
    if (occupant) {
      this.pickPiece(occupant.id);
      return;
    }
    const target = s.cellTargets.find((t) => t.x === x && t.y === y);
    if (target && s.selectedPieceId !== null)
      this.play({ type: 'move', pieceId: s.selectedPieceId, x, y });
    else this.select(null);
  }

  /** Start casting a spell; the following picks choose its targets. */
  beginSpell(spell: SpellId, character?: string): void {
    if (this.blocked) return;
    if (spell === 'shift-time') {
      this.play({ type: 'spell', spell });
      return;
    }
    // Whoever is selected is the caster; without one there is nobody to pay for it.
    const casterId = this.snapshot.selectedPieceId;
    if (casterId === null) return;
    const targeting: Targeting = character ? { spell, casterId, character } : { spell, casterId };
    this.update({ selectedPieceId: null, targeting, ...this.targetsFor(targeting) });
  }

  cancelSpell(): void {
    if (this.snapshot.targeting)
      this.update({ targeting: null, cellTargets: [], pieceTargets: [] });
  }

  /**
   * Pauses AI turns, battles and input until the returned release function is called, e.g. while
   * a piece walks to its square. Several holds may overlap; the last release resumes the game.
   */
  hold(): () => void {
    this.holds += 1;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.holds -= 1;
      if (this.holds === 0) this.continueGame();
    };
  }

  /** The arena reports how the pending battle went; the rules take it from there. */
  resolveBattle(result: BattleResult): void {
    const { state, arena } = this.snapshot;
    if (!arena || state.phase !== 'battle') return;
    if (this.online) {
      // The server's result is the one that counts; the arena stepped the same bundles anyway.
      this.arenaDone = true;
      this.update({ arena: null });
      this.pump();
      return;
    }
    this.finishBattle(result, state.rng);
  }

  /** Locks the current local player's hidden support choice in Card mode. */
  commitCardSupport(cardId: SupportCardId | null, role?: CardRole): void {
    const view = this.snapshot.cardClash;
    if (!view || view.phase !== 'choose' || !view.chooser) return;
    if (cardId !== null && !view.hand.some((card) => card.id === cardId)) return;
    if (this.online) {
      if (this.offline) return;
      this.online.link.sendCardChoice?.(cardId, role);
      this.update({ cardClash: { ...view, chooser: null, hand: [] }, busy: true });
      return;
    }
    this.cardClock.stop();
    this.cardChoices[view.chooser] = { cardId, role };
    this.cardHumans.shift();
    if (this.cardHumans.length > 0) {
      this.showCardChooser();
      return;
    }
    // The people have chosen; an AI still over its card is waited for, as a person would be.
    this.update({
      cardClash: { ...view, chooser: null, hand: [], choosing: [...this.aiChoosing] },
      cardClock: null,
    });
    this.revealWhenChosen();
  }

  /** Reveals the clash once every side has chosen: the people here, and the AI's moment over. */
  private revealWhenChosen(): void {
    if (this.cardHumans.length === 0 && this.aiChoosing.size === 0) this.revealCardClash();
  }

  private stopAiChoices(): void {
    for (const cancel of this.aiChoiceTimers) cancel();
    this.aiChoiceTimers = [];
    this.aiChoosing.clear();
  }

  /**
   * A person here has read the revealed cards: the game goes on. Online it goes on once everyone
   * in the clash has, or their time is up.
   */
  continueCardReveal(): void {
    const view = this.snapshot.cardClash;
    if (view?.phase !== 'reveal' || view.hold !== 'continue') return;
    // Pressed while the clash is being told, it shows the result at once; the next press goes on.
    if (view.story && view.story.step < storyEnd(view.story)) {
      this.stopStory();
      this.update({ cardClash: { ...view, story: { ...view.story, step: storyEnd(view.story) } } });
      return;
    }
    if (this.online) {
      this.online.link.sendCardContinue?.();
      this.update({ cardClash: { ...view, hold: 'others' } });
      return;
    }
    this.settleCardReveal();
  }

  /** Online: the person is ready to play again, and takes their seat back from the AI. */
  reclaimSeat(): void {
    if (this.snapshot.online?.standIn) this.online?.link.reclaim?.();
  }

  /**
   * Whether the person on turn here may offer a draw now (D-46): on their own turn in play, once a
   * turn, with no offer open.
   */
  canOfferDraw(): boolean {
    const { state, busy, draw, online } = this.snapshot;
    if (!this.isHumanTurn() || busy || this.holds > 0 || draw || this.offline) return false;
    if (online && (online.spectator || online.standIn || online.ended)) return false;
    return this.drawOfferedOn !== state.turn;
  }

  /**
   * The person on turn offers the other sides a draw. Each person asked answers it; the AI answers
   * for its sides after a moment, accepting only when it is not ahead (`acceptsDraw`). Once every
   * side has accepted the game ends drawn; a decline, or the offerer playing on, takes it off.
   */
  offerDraw(): void {
    if (!this.canOfferDraw()) return;
    const { state } = this.snapshot;
    this.drawOfferedOn = state.turn;
    if (this.online) {
      // The server asks the others and tells everyone, this device included.
      this.online.link.sendDrawOffer?.();
      return;
    }
    const asked = state.factions
      .filter((f) => !f.eliminated && f.id !== state.activeFaction)
      .map((f) => f.id);
    this.drawPending = { from: state.activeFaction, waiting: new Set(asked) };
    this.showDraw(`${sideName(this.snapshot, state.activeFaction)} offers a draw`);
    this.answerDrawForAi();
  }

  /** A person here answers the draw offered to their side. */
  answerDraw(accept: boolean): void {
    const draw = this.snapshot.draw;
    if (!draw?.asking) return;
    if (this.online) {
      if (this.offline) return;
      this.online.link.sendDrawAnswer?.(accept);
      this.update({ draw: { ...draw, asking: null } });
      return;
    }
    this.drawAnswered(draw.asking, accept);
  }

  /** Commits one reachable main to a Muster clash, or passes the response. */
  respondToMuster(pieceId: PieceId | null): void {
    const view = this.snapshot.muster;
    if (!view || (pieceId !== null && !view.pieces.some((piece) => piece.id === pieceId))) return;
    if (this.offline) return;
    const move: Move =
      pieceId === null ? { type: 'pass-battle' } : { type: 'join-battle', pieceId };
    this.update({ muster: null });
    this.play(move);
  }

  /**
   * Every spell, in a fixed order, and how it stands for the piece now selected. The tablet keeps
   * all of them on show in the same places whatever is selected — a panel whose buttons move about
   * cannot be learned — and colours them by these three flags:
   *
   * - `known` false: this character is no magician of that kind. Greyed.
   * - `known`, not `affordable`: it knows the spell and cannot pay for it. Lit red.
   * - `castable`: known, paid for, and something to aim it at. Lit green.
   *
   * Shift Time is nobody's spell and always castable: it is the player letting the round pass.
   */
  spellPanel(): SpellButton[] {
    const { ctx, state, selectedPieceId } = this.snapshot;
    const selected = selectedPieceId === null ? undefined : pieceById(state, selectedPieceId);
    const playing = state.phase === 'play';
    const mine = selected && selected.faction === state.activeFaction ? selected : undefined;
    const onCircle = !!mine && (cellAt(ctx.board, mine.x, mine.y)?.powerPoint ?? false);
    const held = !!mine && (mine.imprisoned || mine.frozen > 0);
    const hasTarget = new Set(
      playing
        ? spellMoves(ctx, state)
            .filter(
              (m) => m.type === 'spell' && (m.spell === 'shift-time' || m.casterId === mine?.id),
            )
            .map((m) => (m.type === 'spell' ? m.spell : ''))
        : [],
    );
    return ALL_SPELLS.map((spell) => {
      const known = spell === 'shift-time' || knowsSpell(ctx.roster[mine?.character ?? ''], spell);
      const affordable = spell === 'shift-time' || (!!mine && canAfford(mine, spell));
      const castable = playing && known && affordable && hasTarget.has(spell);
      // Why it cannot be cast, most telling reason first, so the panel can say so rather than
      // leaving a player to work out which of several things is in the way.
      const blocked: SpellBlock | null = !known
        ? 'unknown'
        : castable || spell === 'shift-time'
          ? null
          : held
            ? 'held'
            : onCircle
              ? 'circle'
              : !affordable
                ? 'poor'
                : 'no-target';
      return { spell, cost: SPELL_COSTS[spell], known, affordable, castable, blocked };
    });
  }

  /** How much magic the selected piece is carrying, and the most it could; `null` for a non-caster. */
  selectedMagic(): { points: number; most: number } | null {
    const { ctx, state, selectedPieceId } = this.snapshot;
    const piece = selectedPieceId === null ? undefined : pieceById(state, selectedPieceId);
    if (!piece) return null;
    const most = maxSpellPoints(ctx.roster[piece.character]);
    return most === 0 ? null : { points: piece.spellPoints, most };
  }

  private select(pieceId: number | null): void {
    const { ctx, state } = this.snapshot;
    const piece = pieceId === null ? undefined : pieceById(state, pieceId);
    const cellTargets: CellTarget[] = piece
      ? destinationsFor(ctx, state, piece).map((d) => ({
          x: d.x,
          y: d.y,
          kind: d.attacks ? 'attack' : 'move',
        }))
      : [];
    this.update({
      selectedPieceId: piece ? piece.id : null,
      targeting: null,
      cellTargets,
      pieceTargets: [],
    });
  }

  private targetsFor(targeting: Targeting): Pick<SessionSnapshot, 'cellTargets' | 'pieceTargets'> {
    const { ctx, state } = this.snapshot;
    const moves = spellMoves(ctx, state).filter(
      (m) => m.type === 'spell' && m.spell === targeting.spell,
    );
    const cells = new Map<string, CellTarget>();
    const pieces = new Set<number>();
    for (const m of moves) {
      if (m.type !== 'spell') continue;
      switch (m.spell) {
        case 'teleport':
          if (targeting.pieceId === undefined) pieces.add(m.pieceId);
          else if (m.pieceId === targeting.pieceId)
            cells.set(`${m.x},${m.y}`, { x: m.x, y: m.y, kind: 'spell' });
          break;
        case 'heal':
        case 'imprison':
        case 'curse':
        case 'freeze':
        case 'release':
          pieces.add(m.pieceId);
          break;
        case 'exchange':
          if (targeting.pieceId === undefined) {
            pieces.add(m.pieceId);
            pieces.add(m.otherId);
          } else if (m.pieceId === targeting.pieceId) pieces.add(m.otherId);
          else if (m.otherId === targeting.pieceId) pieces.add(m.pieceId);
          break;
        case 'revive':
          if (m.character === targeting.character)
            cells.set(`${m.x},${m.y}`, { x: m.x, y: m.y, kind: 'spell' });
          break;
        case 'summon-elemental':
          // The enemy first, then the square beside it the elemental appears on.
          if (targeting.pieceId === undefined) pieces.add(m.pieceId);
          else if (m.pieceId === targeting.pieceId)
            cells.set(`${m.x},${m.y}`, { x: m.x, y: m.y, kind: 'spell' });
          break;
        case 'shift-time':
          break;
      }
    }
    return { cellTargets: [...cells.values()], pieceTargets: [...pieces] };
  }

  private advanceTargeting(pick: { pieceId?: number; x?: number; y?: number }): void {
    const t = this.snapshot.targeting!;
    switch (t.spell) {
      // One piece is the whole target: a friend to heal or release, an enemy to hold or curse.
      case 'heal':
      case 'imprison':
      case 'curse':
      case 'freeze':
      case 'release':
        if (pick.pieceId !== undefined)
          this.play({ type: 'spell', spell: t.spell, casterId: t.casterId, pieceId: pick.pieceId });
        return;
      case 'teleport':
        if (t.pieceId === undefined && pick.pieceId !== undefined) {
          const targeting = { ...t, pieceId: pick.pieceId };
          this.update({ targeting, ...this.targetsFor(targeting) });
        } else if (t.pieceId !== undefined && pick.x !== undefined && pick.y !== undefined) {
          this.play({
            type: 'spell',
            spell: 'teleport',
            casterId: t.casterId,
            pieceId: t.pieceId,
            x: pick.x,
            y: pick.y,
          });
        }
        return;
      case 'exchange':
        if (t.pieceId === undefined && pick.pieceId !== undefined) {
          const targeting = { ...t, pieceId: pick.pieceId };
          this.update({ targeting, ...this.targetsFor(targeting) });
        } else if (t.pieceId !== undefined && pick.pieceId !== undefined) {
          this.play({
            type: 'spell',
            spell: 'exchange',
            casterId: t.casterId,
            pieceId: t.pieceId,
            otherId: pick.pieceId,
          });
        }
        return;
      case 'revive':
        if (t.character && pick.x !== undefined && pick.y !== undefined)
          this.play({
            type: 'spell',
            spell: 'revive',
            casterId: t.casterId,
            character: t.character,
            x: pick.x,
            y: pick.y,
          });
        return;
      case 'summon-elemental':
        if (t.pieceId === undefined && pick.pieceId !== undefined) {
          const targeting = { ...t, pieceId: pick.pieceId };
          const targets = this.targetsFor(targeting);
          // With a single free square there is nothing to choose: the elemental appears there.
          const only = targets.cellTargets.length === 1 ? targets.cellTargets[0] : undefined;
          if (only)
            this.play({
              type: 'spell',
              spell: 'summon-elemental',
              casterId: t.casterId,
              pieceId: pick.pieceId,
              x: only.x,
              y: only.y,
            });
          else this.update({ targeting, ...targets });
        } else if (t.pieceId !== undefined && pick.x !== undefined && pick.y !== undefined) {
          this.play({
            type: 'spell',
            spell: 'summon-elemental',
            casterId: t.casterId,
            pieceId: t.pieceId,
            x: pick.x,
            y: pick.y,
          });
        }
        return;
      case 'shift-time':
        return;
    }
  }

  private play(move: Move): void {
    const { ctx, state } = this.snapshot;
    // A draw offered this turn lapses when the one who offered it plays on.
    if (move.type !== 'draw' && this.drawPending) this.endDraw();
    if (this.online) {
      // Sent, not applied: the move counts once the server echoes it back in `moved`.
      this.online.link.sendMove(move);
      this.update({
        selectedPieceId: null,
        targeting: null,
        cellTargets: [],
        pieceTargets: [],
        busy: true,
      });
      return;
    }
    const next =
      this.fights === 'card'
        ? replenishCards(ctx, applyMove(ctx, state, move))
        : applyMove(ctx, state, move);
    this.update({
      state: next,
      selectedPieceId: null,
      targeting: null,
      cellTargets: [],
      pieceTargets: [],
      lastMove: move,
      lastEvent: this.describe(state, next, move),
    });
    this.continueGame();
  }

  /** Runs AI turns and battle resolution until a human needs to act or the game ends. */
  private continueGame(): void {
    // Card selection/reveal owns its timer. A presentation hold ending must not cancel it.
    if (!this.online && this.snapshot.state.phase === 'battle' && this.snapshot.cardClash) return;
    this.cancelTimer?.();
    this.cancelTimer = null;
    if (this.online) {
      this.pump();
      return;
    }
    const { ctx, state, seats } = this.snapshot;
    if (this.holds > 0) {
      this.update({ busy: true });
      return;
    }
    if (state.phase === 'ended') {
      this.update({ busy: false });
      return;
    }
    if (state.phase === 'battle') {
      if (this.fights === 'card') {
        if (!this.snapshot.cardClash) this.startCardClash();
        return;
      }
      const arena = this.arenaFor(state);
      if (arena) {
        this.update({ busy: true, arena });
        return;
      }
      this.update({ busy: true });
      this.cancelTimer = this.schedule(() => {
        const current = this.snapshot.state;
        const settled =
          this.fights === 'roll'
            ? resolveRollBattle(ctx, current, {
                levels: Object.fromEntries(
                  Object.entries(this.ai).map(([faction, ai]) => [
                    faction,
                    duelLevelForSkill(ai.skill),
                  ]),
                ),
              })
            : autoResolveBattle(ctx, current);
        const { result, rng } = settled;
        this.finishBattle(result, rng);
      }, this.battleDelay);
      return;
    }
    if (state.phase === 'muster') {
      this.continueMuster();
      return;
    }
    if (seats[state.activeFaction] === 'ai') {
      this.update({ busy: true });
      // A think already under way answers for this turn: a hold let go meanwhile (a menu closed,
      // a piece done walking) must not start a second one (client-session:bugs#4).
      if (this.thinking) return;
      this.cancelTimer = this.schedule(() => {
        const current = this.snapshot.state;
        const token = ++this.thinkToken;
        const depth = this.ai[current.activeFaction]?.depth ?? this.aiDepth;
        // Three to four seconds as a rule, never more than eight (D-36).
        const answer = this.brain(ctx, current, {
          ...boardAiOptions(depth),
          ...boardAiClock(depth),
        });
        // The plies the level counts as its own; Master's fourth is extra (D-36).
        const wanted = boardAiClock(depth).minDepth;
        const play = ({ move, quick, depth: reached }: BrainResult) => {
          this.thinking = false;
          // On a slow device a level can fall short of its own depth inside the clock (D-36).
          if (!quick && reached > 0 && reached < wanted)
            console.info(`The AI looked ${reached} of its ${wanted} plies ahead in time.`);
          // A stale answer (new game, disposed session, state moved on) is dropped.
          if (token !== this.thinkToken || this.snapshot.state !== current) return;
          const next =
            this.fights === 'card'
              ? replenishCards(ctx, applyMove(ctx, current, move))
              : applyMove(ctx, current, move);
          const event = this.describe(current, next, move);
          this.update({
            state: next,
            busy: false,
            lastMove: move,
            // Say so when the AI could not think it through: its move is only a one-ply look.
            lastEvent: quick
              ? { ...event, text: `${event.text} (a quick move: the AI could not think)` }
              : event,
          });
          this.continueGame();
        };
        if (answer instanceof Promise) {
          this.thinking = true;
          void answer.then(play, (error: unknown) => {
            this.thinking = false;
            if (token !== this.thinkToken || this.snapshot.state !== current) return;
            // Cancelled from outside while still wanted (another game gave up its think in the
            // brain they share): think again.
            if (error instanceof CancelledThink) this.continueGame();
            // Even the quick move failing must not leave the table waiting: the side passes.
            else play({ move: timeoutMove(current), quick: true, score: 0, depth: 0, nodes: 0 });
          });
        } else play(answer);
      }, this.aiDelay);
      return;
    }
    this.update({ busy: false });
  }

  private startCardClash(): void {
    const { ctx, seats } = this.snapshot;
    const state = initializeCards(ctx, this.snapshot.state);
    const pending = state.pendingBattle!;
    const mains = (pending.participantIds ?? [pending.attackerId, pending.defenderId]).map((id) =>
      pieceById(state, id)!,
    );
    const mainScores = cardBattleMainScores(
      ctx,
      state,
      mains.map((piece) => piece.id),
    );
    this.cardChoices = {};
    this.cardHumans = [];
    this.stopAiChoices();
    // With a person in the clash, the AI takes its moment over a card; between AI sides, none.
    const person = !this.online && mains.some((piece) => seats[piece.faction] === 'human');
    for (const piece of mains) {
      if (!this.online && seats[piece.faction] === 'human') this.cardHumans.push(piece.faction);
      else {
        this.cardChoices[piece.faction] = chooseCardSupport(
          ctx,
          state,
          piece,
          ctx.cardLevels?.[piece.faction] ?? 'expert',
        );
        if (person) this.aiChoosing.add(piece.faction);
      }
    }
    this.update({
      state,
      busy: true,
      cardClash: {
        phase: 'choose',
        participants: mains.map((piece, i) => ({
          pieceId: piece.id,
          faction: piece.faction,
          character: piece.character,
          name: ctx.roster[piece.character]?.name ?? piece.character,
          main: mainScores[i]!,
          level: ctx.cardLevels?.[piece.faction] ?? 'expert',
        })),
        chooser: null,
        hand: [],
        choosing: [...this.cardHumans, ...this.aiChoosing],
      },
    });
    [...this.aiChoosing].forEach((faction, i) =>
      this.aiChoiceTimers.push(
        this.schedule(
          () => {
            this.aiChoosing.delete(faction);
            const view = this.snapshot.cardClash;
            if (view?.phase === 'choose')
              this.update({
                cardClash: {
                  ...view,
                  choosing: (view.choosing ?? []).filter((f) => f !== faction),
                },
              });
            this.revealWhenChosen();
          },
          // A moment of its own, the same for the same position: a person does not choose at once.
          AI_CARD_CHOICE_MS.min + (((state.rng >>> 0) + i * 7919) % AI_CARD_CHOICE_MS.spread),
        ),
      ),
    );
    if (this.cardHumans.length > 0) this.showCardChooser();
    else this.revealWhenChosen();
  }

  private continueMuster(): void {
    const { ctx, state, seats } = this.snapshot;
    const faction = state.pendingBattle?.responders?.[state.pendingBattle.responseIndex ?? 0];
    if (!faction) return;
    const moves = legalMoves(ctx, state);
    if (seats[faction] === 'human') {
      this.update({
        busy: false,
        muster: {
          faction: faction!,
          pieces: moves
            .filter(
              (move): move is Extract<Move, { type: 'join-battle' }> => move.type === 'join-battle',
            )
            .map((move) => {
              const piece = pieceById(state, move.pieceId)!;
              return {
                id: piece.id,
                character: piece.character,
                name: ctx.roster[piece.character]?.name ?? piece.character,
              };
            }),
        },
      });
      return;
    }
    this.update({ busy: true, muster: null });
    this.cancelTimer = this.schedule(() => {
      const current = this.snapshot.state;
      const move = chooseMusterResponse(ctx, current);
      const next = applyMove(ctx, current, move);
      this.update({ state: next, lastMove: move, lastEvent: this.describe(current, next, move) });
      this.continueGame();
    }, this.aiDelay);
    return;
  }

  /**
   * Stops the Card clash's clock, or lets it run on: while one device is handed from one person
   * to the next, or a page is open over the game, neither the seconds to choose nor a reveal's
   * run against cards nobody is looking at. Online the server keeps the time.
   */
  holdCardClock(held: boolean): void {
    // The telling of a clash waits for the eyes as well, online too: it is only shown here.
    this.storyHeld = held;
    if (held) this.stopStory();
    else this.tellStory();
    if (this.online) return;
    const clock = this.cardClock.hold(held);
    if (clock) this.update({ cardClock: clock });
  }

  /** Moves a told clash on by a step when its time is up, on the session's clock. */
  private tellStory(): void {
    this.stopStory();
    const story = this.snapshot.cardClash?.story;
    if (!story || story.step >= storyEnd(story) || this.storyHeld) return;
    this.storyTimer = this.schedule(
      () => {
        this.storyTimer = null;
        const view = this.snapshot.cardClash;
        if (view?.story !== story) return;
        this.update({ cardClash: { ...view, story: { ...story, step: story.step + 1 } } });
        this.tellStory();
      },
      msAfterStep(story, story.step),
    );
  }

  private stopStory(): void {
    this.storyTimer?.();
    this.storyTimer = null;
  }

  private showCardChooser(): void {
    const { ctx, state, cardClash } = this.snapshot;
    const faction = this.cardHumans[0]!;
    const main = state.pendingBattle
      ? (
          state.pendingBattle.participantIds ?? [
            state.pendingBattle.attackerId,
            state.pendingBattle.defenderId,
          ]
        )
          .map((id) => pieceById(state, id)!)
          .find((piece) => piece.faction === faction)
      : undefined;
    if (!cardClash || !main) return;
    const hand = (state.cards?.factions[faction]?.hand ?? [])
      .filter((id) => eligibleSupport(ctx, state, main, id))
      .map((id) => ({
        id,
        name: supportCardName(ctx, id),
        role: supportRole(id, undefined, ctx),
        water: id === 'element:water',
        fit: supportFitDescription(id, ctx),
      }));
    // A lone person against the AI has no hand to hide from anyone: longer to read the cards.
    const people = Object.values(this.snapshot.seats).filter((seat) => seat === 'human').length;
    const seconds = people === 1 ? CARD_CHOICE_SECONDS_ALONE : CARD_CHOICE_SECONDS;
    this.update({
      cardClash: { ...cardClash, chooser: faction, hand },
      cardClock: this.cardClock.start(seconds, () => this.commitCardSupport(null)),
    });
  }

  private revealCardClash(): void {
    const { ctx, state, seats, cardClash } = this.snapshot;
    if (!cardClash) return;
    const settled = resolveCardBattle(ctx, state, this.cardChoices);
    this.revealed = { result: settled.result, rng: settled.rng };
    // The people in the clash watch it told, then read its result until they continue, for as
    // long as they like at one table (the owner's request of 2026-09-25; THE_DECISION_TABLE #3).
    // A clash between AI sides passes in a beat, told at once.
    const person = cardClash.participants.some((p) => seats[p.faction] === 'human');
    const story = tellCardClash(
      ctx,
      state,
      settled.scores,
      cardClash.participants.map((p) => p.pieceId),
    );
    this.cardClock.stop();
    this.update({
      state: { ...state, rng: settled.rng, cards: settled.cards },
      cardClash: {
        phase: 'reveal',
        participants: cardClash.participants,
        chooser: null,
        hand: [],
        scores: settled.scores,
        story: person ? story : { ...story, step: storyEnd(story) },
        ...(person ? { hold: 'continue' as const } : {}),
      },
      cardClock: null,
    });
    if (person) this.tellStory();
    else this.cancelTimer = this.schedule(() => this.settleCardReveal(), this.battleDelay);
  }

  private settleCardReveal(): void {
    const revealed = this.revealed;
    if (!revealed) return;
    this.cancelTimer?.();
    this.cancelTimer = null;
    this.finishBattle(revealed.result, revealed.rng);
  }

  /**
   * How the pending battle is fought: in the arena with the local person on one side, in the
   * arena with the AI on both sides (`watch`), or `null` to roll it instead.
   */
  private arenaFor(state: GameState): ArenaBattle | null {
    const battle = state.pendingBattle;
    if (this.fights === 'roll' || this.fights === 'card' || !battle) return null;
    const factionOf = (id: PieceId) => pieceById(state, id)!.faction;
    const seatOf = (id: PieceId) => this.snapshot.seats[factionOf(id)];
    const sides: { side: FighterId; id: PieceId }[] = [
      { side: 'attacker', id: battle.attackerId },
      { side: 'defender', id: battle.defenderId },
    ];
    const people = this.fights === 'watch' ? [] : sides.filter((s) => seatOf(s.id) === 'human');
    // Two people with a controller each both fight. Sharing the device, only the first (the
    // attacker's person) can, and the AI stands in for the other, at a fair level.
    const bound = Object.keys(this.snapshot.devices ?? {}).length > 0;
    const humans = (bound ? people : people.slice(0, 1)).map((s) => s.side);
    const humanSide = humans[0] ?? null;
    if (!humanSide && this.fights !== 'watch') return null;
    const skillOf = (id: PieceId, side: FighterId): ArenaAiSkill | undefined =>
      this.ai[factionOf(id)]?.skill ?? (humans.includes(side) ? undefined : duelLevelSkill('fair'));
    const skills: Partial<Record<FighterId, ArenaAiSkill>> = {};
    const attacker = skillOf(battle.attackerId, 'attacker');
    const defender = skillOf(battle.defenderId, 'defender');
    if (attacker) skills.attacker = attacker;
    if (defender) skills.defender = defender;
    const devices: Partial<Record<FighterId, SeatDevice>> = {};
    for (const { side, id } of sides) {
      const device = this.snapshot.devices?.[factionOf(id)];
      if (humans.includes(side) && device) devices[side] = device;
    }
    return {
      attackerId: battle.attackerId,
      defenderId: battle.defenderId,
      humanSide,
      humans,
      devices: bound ? devices : undefined,
      seed: (state.rng ^ Math.imul(state.turn, 0x9e3779b1)) >>> 0,
      humanFriendlyAi: this.humanFriendlyAi,
      skills,
    };
  }

  private finishBattle(result: BattleResult, rng: number): void {
    this.revealed = null;
    this.stopStory();
    this.stopAiChoices();
    this.cardClock.stop();
    const { ctx, state: current } = this.snapshot;
    const battle = current.pendingBattle!;
    const winner = result.winnerId === null ? null : pieceById(current, result.winnerId);
    const loser = (battle.participantIds ?? [battle.attackerId, battle.defenderId])
      .filter((id) => id !== result.winnerId)
      .map((id) => pieceById(current, id)!);
    const resolved = applyBattleResult(ctx, current, result, rng);
    const next = this.fights === 'card' ? replenishCards(ctx, resolved) : resolved;
    // Sides by the board's names, never their ids (client-session:quality#2).
    const text = winner
      ? `${this.name(winner.character)} (${boardSideName(ctx, winner.faction)}) defeats ${loser.map((p) => this.name(p.character)).join(' and ')}`
      : `${loser.map((p) => this.name(p.character)).join(' and ')} destroy each other`;
    this.update({
      state: next,
      arena: null,
      cardClash: null,
      cardClock: null,
      lastMove: null,
      lastEvent: this.event(next.phase === 'ended' ? 'ended' : 'battle-resolved', text),
    });
    if (!this.online) this.continueGame();
  }

  /** Shows the open offer, asking the first side a person here answers for. */
  private showDraw(text?: string): void {
    const pending = this.drawPending;
    const draw = pending && {
      from: pending.from,
      waiting: [...pending.waiting],
      asking: [...pending.waiting].find((f) => this.snapshot.seats[f] === 'human') ?? null,
    };
    this.update(text ? { draw, lastEvent: this.event('draw', text) } : { draw });
  }

  /** An answer to the offer here: a decline takes it off, the last acceptance ends the game. */
  private drawAnswered(faction: FactionId, accept: boolean): void {
    const pending = this.drawPending;
    if (!pending?.waiting.delete(faction)) return;
    if (!accept) {
      this.endDraw(`${sideName(this.snapshot, faction)} declines the draw`);
      return;
    }
    if (pending.waiting.size > 0) {
      this.showDraw();
      return;
    }
    this.endDraw();
    this.play({ type: 'draw' });
  }

  private endDraw(text?: string): void {
    this.drawTimer?.();
    this.drawTimer = null;
    this.drawPending = null;
    this.update(text ? { draw: null, lastEvent: this.event('draw', text) } : { draw: null });
  }

  /** The AI's answer for the sides it plays, a moment after the offer. */
  private answerDrawForAi(): void {
    const pending = this.drawPending;
    if (!pending || ![...pending.waiting].some((f) => this.snapshot.seats[f] === 'ai')) return;
    this.drawTimer = this.schedule(() => {
      this.drawTimer = null;
      for (const faction of [...pending.waiting]) {
        if (this.drawPending !== pending) return;
        if (this.snapshot.seats[faction] !== 'ai') continue;
        const { ctx, state } = this.snapshot;
        this.drawAnswered(faction, acceptsDraw(ctx, state, faction));
      }
    }, AI_DRAW_ANSWER_MS);
  }

  private onRoomEvent(event: RoomEvent): void {
    const online = this.snapshot.online!;
    switch (event.t) {
      case 'moved':
      case 'battleEnded':
        this.received += 1;
        this.inbox.push(event);
        this.pump();
        return;
      case 'seatChanged': {
        // The panes follow at once; who moves changes in turn with the moves around it.
        const standIns = online.standIns.filter((seat) => seat !== event.seat);
        if (event.standIn) standIns.push(event.seat);
        this.update({
          online: {
            ...online,
            standIns,
            standIn: standIns.includes(this.online!.link.seat),
            paused: online.paused.filter((dropped) => dropped.seat !== event.seat),
          },
        });
        this.inbox.push(event);
        this.pump();
        return;
      }
      case 'battle':
        this.announceDuel(event, []);
        this.pump();
        return;
      case 'drawOffer': {
        const factions = this.snapshot.ctx.board.def.factions;
        const from = factions[event.seat]?.id;
        if (!from) return;
        const waiting = event.waiting.flatMap((seat) => factions[seat]?.id ?? []);
        const mine = factions[this.online!.link.seat]?.id;
        const earlier = this.snapshot.draw;
        // Sent again as each side accepts: the ones that have answered are off the list.
        const asking = mine && waiting.includes(mine) && !online.standIn ? mine : null;
        this.update({
          draw: { from, waiting, asking },
          ...(earlier?.from === from
            ? {}
            : { lastEvent: this.event('draw', `${sideName(this.snapshot, from)} offers a draw`) }),
        });
        return;
      }
      case 'drawDeclined': {
        const by =
          event.by === null ? undefined : this.snapshot.ctx.board.def.factions[event.by]?.id;
        this.update({
          draw: null,
          ...(by
            ? { lastEvent: this.event('draw', `${sideName(this.snapshot, by)} declines the draw`) }
            : {}),
        });
        return;
      }
      case 'tick':
        this.duel?.buffer.push(event);
        return;
      case 'cardPrompt':
      case 'cardReveal':
        // In turn with the moves before them: a clash told before its attack is on this board
        // has no battle to tell, as when the server's clash overtook moves still walking here
        // after a reconnection.
        if (event.t === 'cardPrompt') this.promptedAt.set(event, performance.now());
        this.inbox.push(event);
        this.pump();
        return;
      case 'sync': {
        // A reconnection replays everything; only what this client has not seen is new.
        for (const entry of event.history.slice(this.received)) {
          this.inbox.push(
            entry.kind === 'move'
              ? { t: 'moved', move: entry.move, by: entry.by }
              : { t: 'battleEnded', result: entry.result, rng: entry.rng },
          );
        }
        this.received = Math.max(this.received, event.history.length);
        this.standIns(event.standIns ?? []);
        this.waitingFor(event.paused ?? []);
        // An offer still open is asked again after the catch-up; one that is not is gone.
        if (this.snapshot.draw) this.update({ draw: null });
        if (this.snapshot.online!.lostAt !== null)
          this.update({ online: { ...this.snapshot.online!, lostAt: null } });
        if (event.battle) {
          if (this.duel?.start.seed !== event.battle.seed) this.announceDuel(event.battle, []);
          for (const tick of event.battle.ticks) this.duel?.buffer.push(tick);
        }
        this.pump();
        return;
      }
      case 'clock':
        this.update({
          online: {
            ...online,
            clock: {
              seat: event.seat,
              name: this.seatName(event.seat),
              secondsLeft: event.secondsLeft,
              mine: event.seat === this.online!.link.seat,
            },
          },
        });
        return;
      case 'paused':
        this.update({
          online: {
            ...online,
            clock: null,
            paused: [
              ...online.paused.filter((dropped) => dropped.seat !== event.seat),
              {
                seat: event.seat,
                name: this.seatName(event.seat),
                secondsLeft: event.secondsLeft,
                at: performance.now(),
              },
            ],
          },
        });
        return;
      case 'resumed':
        this.update({
          online: {
            ...online,
            paused: online.paused.filter((dropped) => dropped.seat !== event.seat),
          },
        });
        return;
      case 'ended':
        this.update({ online: { ...online, clock: null } });
        if (event.reason !== 'finished')
          this.update({
            online: { ...this.snapshot.online!, ended: event.reason, paused: [] },
            busy: true,
          });
        return;
      case 'error':
        this.update({ online: { ...online, notice: event.message } });
        // A refused move: the player may try another. A refused muster answer gets its prompt
        // back, where it used to leave the board busy with the prompt gone
        // (client-session:quality#14).
        if (this.snapshot.state.phase === 'play' || this.snapshot.state.phase === 'muster')
          this.pump();
        return;
      case 'connection':
        this.update({
          online: {
            ...online,
            connection: event.state,
            notice: event.state === 'closed' ? (event.detail ?? online.notice) : online.notice,
            // The countdown on this person's own screen runs from the moment the line went, and
            // the catch-up that follows a resume ends it.
            lostAt:
              event.state === 'reconnecting' ? (online.lostAt ?? performance.now()) : online.lostAt,
          },
        });
        return;
      default:
        return;
    }
  }

  /**
   * The seats the AI stands in for, as a catch-up lists them: the panes follow at once, the seats
   * in turn with the moves the catch-up replays before them.
   */
  private standIns(seats: number[]): void {
    const online = this.snapshot.online;
    if (!online) return;
    const before = online.standIns;
    for (const seat of seats)
      if (!before.includes(seat))
        this.inbox.push({ t: 'seatChanged', seat, kind: 'ai', standIn: true });
    for (const seat of before)
      if (!seats.includes(seat)) this.inbox.push({ t: 'seatChanged', seat, kind: 'human' });
    this.update({
      online: {
        ...online,
        standIns: [...seats],
        standIn: seats.includes(this.online!.link.seat),
        paused: online.paused.filter((dropped) => !seats.includes(dropped.seat)),
      },
    });
  }

  /** The countdowns a catch-up says are running: the server's word replaces what was known. */
  private waitingFor(seats: { seat: number; secondsLeft: number }[]): void {
    const online = this.snapshot.online;
    if (!online) return;
    const at = performance.now();
    this.update({
      online: {
        ...online,
        paused: seats.map(({ seat, secondsLeft }) => ({
          seat,
          name: this.seatName(seat),
          secondsLeft,
          at,
        })),
      },
    });
  }

  /** The server announced a duel: its bundles are collected from now on, whatever the view does. */
  /** The server asks this client's chooser for a support card. */
  private showCardPrompt(event: Extract<ServerMessage, { t: 'cardPrompt' }>): void {
    // The server's clock ran while the prompt waited behind the moves before it.
    const waited = (performance.now() - (this.promptedAt.get(event) ?? performance.now())) / 1000;
    const secondsLeft = Math.max(
      0,
      Math.round((event.secondsLeft ?? CARD_CHOICE_SECONDS) - waited),
    );
    this.update({
      busy: false,
      cardClash: {
        phase: 'choose',
        participants: event.participants,
        chooser: event.faction,
        hand: event.hand,
      },
      // The server keeps the time; this only shows it.
      cardClock: this.cardClock.start(secondsLeft, null, CARD_CHOICE_SECONDS),
    });
  }

  /** The server reveals a clash's cards: told from the state it was resolved from. */
  private showCardReveal(event: Extract<ServerMessage, { t: 'cardReveal' }>): void {
    const online = this.snapshot.online!;
    const existing = this.snapshot.cardClash;
    const state = this.snapshot.state;
    const pending = state.pendingBattle;
    // Nothing to tell without the battle it settles, whatever the order things came in.
    if (!pending) return;
    const ids =
      pending?.participantIds ?? (pending ? [pending.attackerId, pending.defenderId] : []);
    const main = cardBattleMainScores(this.snapshot.ctx, state, ids);
    const participants =
      existing?.participants ??
      ids.map((id, i) => {
        const piece = pieceById(state, id)!;
        return {
          pieceId: id,
          faction: piece.faction,
          character: piece.character,
          name: this.name(piece.character),
          main: main[i]!,
        };
      });
    // The room waits for the people in the clash to read it, this one among them unless the
    // AI stands in for them.
    const mine = this.snapshot.ctx.board.def.factions[this.online!.link.seat]?.id;
    const reading =
      event.holdSeconds !== undefined &&
      !online.standIn &&
      participants.some((p) => p.faction === mine);
    // Told from the state the server resolved it from, which this client has too: the same
    // chances after each support, and the same draw.
    const story = tellCardClash(
      this.snapshot.ctx,
      state,
      event.scores,
      participants.map((p) => p.pieceId),
    );
    this.cardClock.stop();
    this.update({
      busy: true,
      cardClash: {
        phase: 'reveal',
        participants,
        chooser: null,
        hand: [],
        scores: event.scores,
        story: reading ? story : { ...story, step: storyEnd(story) },
        ...(reading ? { hold: 'continue' as const } : {}),
      },
      cardClock:
        event.holdSeconds !== undefined
          ? this.cardClock.start(event.holdSeconds, null, CARD_REVEAL_SECONDS)
          : null,
    });
    if (reading) this.tellStory();
  }

  private announceDuel(start: BattleStart, ticks: TickBundle[]): void {
    const buffer = new TickBuffer();
    for (const tick of ticks) buffer.push(tick);
    this.duel = {
      start: {
        seed: start.seed,
        attackerId: start.attackerId,
        defenderId: start.defenderId,
        humans: start.humans,
        ...(start.levels ? { levels: start.levels } : {}),
      },
      buffer,
    };
    this.arenaDone = false;
    this.inbox.push({ t: 'battle', ...this.duel.start });
  }

  /**
   * Applies the server's events in order, one turn of the view at a time: a move waits while a
   * piece travels, a duel waits for the arena, and the result of a duel waits until the arena
   * has shown its outcome.
   */
  private pump(): void {
    if (!this.online) return;
    if (this.holds > 0) {
      this.update({ busy: true });
      return;
    }
    while (this.inbox.length > 0) {
      const event = this.inbox[0]!;
      const { ctx, state } = this.snapshot;
      if (event.t === 'moved') {
        let next: GameState;
        try {
          next = applyMove(ctx, state, event.move);
        } catch {
          // Out of step with the server; nothing sensible can follow but a fresh start from it.
          this.inbox = [];
          this.update({
            busy: true,
            online: {
              ...this.snapshot.online!,
              desynced: true,
              notice: 'This screen lost track of the game',
            },
          });
          break;
        }
        this.inbox.shift();
        const online = this.snapshot.online!;
        const mine = event.by === this.online.link.seat;
        this.update({
          state: next,
          // Any move ends an offer: the draw it asked for, or the turn it was made on.
          draw: null,
          selectedPieceId: null,
          targeting: null,
          cellTargets: [],
          pieceTargets: [],
          lastMove: event.move,
          lastEvent: this.describe(state, next, event.move, event.timeout),
          online: {
            ...online,
            // The seat's clock stopped with its move; the next one comes from the server.
            clock: online.clock?.seat === event.by ? null : online.clock,
            notice: event.timeout && mine ? 'Your time ran out: the server moved for you' : null,
          },
        });
        if (this.holds > 0) {
          this.update({ busy: true });
          return;
        }
        continue;
      }
      if (event.t === 'battle') {
        this.inbox.shift();
        const pending = state.pendingBattle;
        if (state.phase !== 'battle' || !pending || !this.duel) continue;
        const myFaction = ctx.board.def.factions[this.online.link.seat]?.id;
        const factionOf = (id: PieceId) => pieceById(state, id)?.faction;
        // A fighter the AI takes for this person while they are away is still theirs to steer:
        // the server heeds their inputs again once they have taken the seat back.
        const away = this.snapshot.online!.standIn && this.fights !== 'watch';
        const humanSide: FighterId | null =
          (event.humans.attacker || away) && factionOf(event.attackerId) === myFaction
            ? 'attacker'
            : (event.humans.defender || away) && factionOf(event.defenderId) === myFaction
              ? 'defender'
              : null;
        this.update({
          busy: true,
          arena: {
            attackerId: event.attackerId,
            defenderId: event.defenderId,
            humanSide,
            humans: humanSide ? [humanSide] : [],
            seed: event.seed,
            lockstep: createLockstepLink(this.duel.buffer, this.online.link),
            levels: event.levels ?? {},
          },
          // Duels are never clocked.
          online: { ...this.snapshot.online!, clock: null },
        });
        return;
      }
      if (event.t === 'battleEnded') {
        if (this.snapshot.arena && !this.arenaDone) return;
        this.inbox.shift();
        this.duel = null;
        this.arenaDone = false;
        if (state.phase !== 'battle') continue;
        this.finishBattle(event.result, event.rng);
        if (this.holds > 0) {
          this.update({ busy: true });
          return;
        }
        continue;
      }
      if (event.t === 'seatChanged') {
        this.inbox.shift();
        const faction = ctx.board.def.factions[event.seat]?.id;
        if (faction === undefined) continue;
        const mine = event.seat === this.online.link.seat;
        const kind: Seat = event.kind === 'ai' ? 'ai' : mine ? 'human' : 'remote';
        const seats: Record<FactionId, Seat> = { ...this.snapshot.seats, [faction]: kind };
        if (event.standIn && mine) {
          // The AI plays this person's seat until they take it back: nothing of theirs to pick.
          this.update({
            seats,
            selectedPieceId: null,
            targeting: null,
            cellTargets: [],
            pieceTargets: [],
          });
          continue;
        }
        if (this.snapshot.seats[faction] !== 'human' || event.kind !== 'ai') {
          this.update({ seats });
          continue;
        }
        // The local seat went to the AI after a run of timeouts: this person watches from now on.
        this.update({
          seats,
          selectedPieceId: null,
          targeting: null,
          cellTargets: [],
          pieceTargets: [],
          online: {
            ...this.snapshot.online!,
            spectator: true,
            notice: 'Your seat went to the AI after three timeouts; you are watching now',
          },
        });
        continue;
      }
      if (event.t === 'cardPrompt') {
        this.inbox.shift();
        // The clash waits for this person's choice; what came after it waits with it.
        this.showCardPrompt(event);
        return;
      }
      if (event.t === 'cardReveal') {
        this.inbox.shift();
        this.showCardReveal(event);
        continue;
      }
      this.inbox.shift();
    }
    const { state } = this.snapshot;
    if (state.phase === 'muster') {
      const faction = state.pendingBattle?.responders?.[state.pendingBattle.responseIndex ?? 0];
      const myFaction = this.snapshot.ctx.board.def.factions[this.online.link.seat]?.id;
      if (faction && faction === myFaction && this.snapshot.seats[faction] === 'human') {
        const moves = legalMoves(this.snapshot.ctx, state);
        this.update({
          busy: false,
          muster: {
            faction: faction!,
            pieces: moves
              .filter(
                (move): move is Extract<Move, { type: 'join-battle' }> =>
                  move.type === 'join-battle',
              )
              .map((move) => {
                const piece = pieceById(state, move.pieceId)!;
                return {
                  id: piece.id,
                  character: piece.character,
                  name: this.name(piece.character),
                };
              }),
          },
        });
        return;
      }
    }
    this.update({ busy: state.phase !== 'ended' && !this.isHumanTurn(), muster: null });
  }

  private describe(
    before: GameState,
    after: GameState,
    move: Move,
    timeout?: boolean,
  ): SessionEvent {
    // The side as people read it, not its id (client-session:quality#2, x-player-journey#16).
    const who = sideName(this.snapshot, before.activeFaction);
    if (after.phase === 'ended') {
      return this.event(
        'ended',
        after.agreedDraw
          ? 'The sides agree to a draw'
          : after.winner === 'draw' || !after.winner
            ? 'The game is a draw'
            : `${sideName(this.snapshot, after.winner)} wins`,
      );
    }
    // A move the clock played for a seat out of time is marked as such.
    const late = timeout ? 'Out of time – ' : '';
    if (move.type === 'pass')
      return this.event(
        'move',
        timeout ? `${who} ran out of time and passes` : `${who} cannot move and passes`,
      );
    // One word for it everywhere, the side by its name (client-tablets-ui:quality#8).
    if (move.type === 'pass-battle') {
      const side = before.pendingBattle?.responders?.[before.pendingBattle.responseIndex ?? 0];
      return this.event(
        'battle',
        `${side ? sideName(this.snapshot, side) : 'A side'} passes the muster`,
      );
    }
    if (move.type === 'join-battle') {
      const joined = pieceById(before, move.pieceId);
      return this.event(
        'battle',
        `${joined ? this.name(joined.character) : 'A fighter'} joins the muster`,
      );
    }
    if (move.type === 'spell')
      return this.event('spell', `${late}${who} casts ${move.spell.replace('-', ' ')}`);
    // An agreed draw ends the game, and is told above.
    if (move.type === 'draw') return this.event('ended', 'The sides agree to a draw');
    const piece = pieceById(before, move.pieceId)!;
    if (after.phase === 'battle' || after.phase === 'muster') {
      const defender = after.pendingBattle
        ? pieceById(after, after.pendingBattle.defenderId)
        : undefined;
      return this.event(
        'battle',
        `${late}${this.name(piece.character)} attacks ${defender ? this.name(defender.character) : 'the enemy'}`,
      );
    }
    return this.event('move', `${late}${who} moves ${this.name(piece.character)}`);
  }

  /** A seat's name as announced at the start, or its faction's name for the AI. */
  private seatName(seat: number): string {
    return (
      this.online?.names[seat] ||
      this.snapshot.ctx.board.def.factions[seat]?.name ||
      `Player ${seat + 1}`
    );
  }

  private name(character: string): string {
    return this.snapshot.ctx.roster[character]?.name ?? character;
  }

  private event(type: SessionEvent['type'], text: string): SessionEvent {
    this.eventId += 1;
    return { id: this.eventId, type, text };
  }

  /** A think in flight is of no use once the game has moved on: its worker is stopped. */
  private stopThinking(): void {
    if (!this.thinking) return;
    this.thinking = false;
    this.brain.cancel?.();
  }

  private update(patch: Partial<SessionSnapshot>): void {
    // Nothing new, nobody told: every listener re-renders, redraws the tablets or saves
    // (client-session:quality#8).
    const keys = Object.keys(patch) as (keyof SessionSnapshot)[];
    if (keys.every((key) => patch[key] === this.snapshot[key])) return;
    if (patch.state && patch.state !== this.snapshot.state) {
      this.thinkToken += 1;
      this.stopThinking();
    }
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }
}
